import React, { useState } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Button } from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import { ConfirmationModal } from "../../../shared/components/common";

const CaseDetail = ({
  caseData,
  currentUserName = "You",
  onBack,
  onSendReply,
  onDownloadAttachment,
  onCloseCase,
  isLoadingThread = false,
  onOpenCase,
  onEditMessage,
  onDeleteMessage,
}) => {
  const [replyMessage, setReplyMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeletingMessageId, setIsDeletingMessageId] = useState(null);
  const [isDownloadingMessageId, setIsDownloadingMessageId] = useState(null);
  const [messageToDeleteId, setMessageToDeleteId] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || isSendingReply) {
      return;
    }

    setIsSendingReply(true);
    const isSent = await onSendReply(caseData.id, replyMessage, attachment);
    setIsSendingReply(false);

    if (isSent) {
      setReplyMessage("");
      setAttachment(null);
      setAttachmentInputKey((prev) => prev + 1);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentInputKey((prev) => prev + 1);
  };

  const startEditing = (message) => {
    setEditingMessageId(message.id);
    setEditingText(message.message || "");
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingText("");
  };

  const saveMessageEdit = async (messageId) => {
    if (!onEditMessage || !messageId) {
      return;
    }

    const nextText = editingText.trim();
    if (!nextText) {
      return;
    }

    setIsSavingEdit(true);
    const isUpdated = await onEditMessage(messageId, nextText);
    setIsSavingEdit(false);

    if (isUpdated) {
      cancelEditing();
    }
  };

  const deleteMessage = async (messageId) => {
    if (!onDeleteMessage || !messageId) {
      return;
    }

    setMessageToDeleteId(messageId);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDeleteId) {
      return;
    }

    setIsDeletingMessageId(messageToDeleteId);
    await onDeleteMessage(messageToDeleteId);
    setIsDeletingMessageId(null);
    if (editingMessageId === messageToDeleteId) {
      cancelEditing();
    }
    setMessageToDeleteId(null);
  };

  const cancelDeleteMessage = () => {
    if (isDeletingMessageId) {
      return;
    }

    setMessageToDeleteId(null);
  };

  const handleAttachmentClick = async (message) => {
    if (!onDownloadAttachment || !message?.id) {
      return;
    }

    setIsDownloadingMessageId(message.id);
    try {
      await onDownloadAttachment(message);
    } finally {
      setIsDownloadingMessageId(null);
    }
  };

  const conversationThread = [];

  conversationThread.push({
    type: "user",
    from: currentUserName,
    date: caseData.date,
    message: caseData.message,
    hasAttachment: false,
  });

  if (caseData.replies) {
    caseData.replies.forEach((reply) => {
      conversationThread.push({ ...reply, type: "user" });
    });
  }

  if (caseData.supportResponses) {
    caseData.supportResponses.forEach((response) => {
      conversationThread.push({ ...response, type: "support" });
    });
  }

  return (
    <div className="space-y-4">
      <Button
          onClick={onBack}
          className="flex items-center gap-2 text-primary hover:opacity-80"
        >
          <ArrowBackIosNew fontSize="small" />
          <span className="text-sm font-semibold">Back to Support & Help</span>
        </Button>

      <div className="bg-white border border-line rounded-xl px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-primary">{caseData.title}</h2>
            <p className="text-xs text-muted mt-1">
              Created: {caseData.date}
            </p>
            <p className="text-xs text-muted">
              Status: <span className="capitalize">{caseData.status}</span>
            </p>
          </div>
          {caseData.status === "open" ? (
            <button
              onClick={() => onCloseCase(caseData.id)}
              className="bg-primary text-white px-4 py-2 rounded-md text-xs font-semibold hover:bg-primary/90 transition-colors"
            >
              CLOSE THIS CASE
            </button>
          ) : (
            <button 
            onClick={() => onOpenCase(caseData.id)}
            className="bg-primary/80 text-white px-3 sm:px-4 py-2 rounded-md text-[10px] sm:text-xs font-semibold tracking-wide">
              RE-OPEN CASE
            </button>
          )
        }
        </div>
      </div>

      <div className="bg-white border border-line rounded-xl px-6 py-4">
        <h3 className="text-sm font-semibold text-primary mb-3">Your Message</h3>
        <div className="border border-line rounded-lg p-4">
          <h4 className="text-sm font-semibold text-primary mb-2">
            Reply to Support
          </h4>
          <textarea
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            placeholder="Type your message here..."
            className="w-full border-0 focus:outline-none text-sm text-content min-h-[150px] resize-none"
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <input
              key={attachmentInputKey}
              type="file"
              id="reply-attachment"
              className="hidden"
              onChange={handleFileChange}
            />
            <label
              htmlFor="reply-attachment"
              className="flex items-center gap-2 text-primary font-medium cursor-pointer hover:underline text-sm"
            >
              <AttachFileIcon style={{ fontSize: "1.1rem" }} />
              <span>Add an attachment</span>
            </label>
            {attachment && (
              <div className="flex items-center gap-2 text-xs text-muted">
                <span>{attachment.name}</span>
                <button
                  type="button"
                  onClick={removeAttachment}
                  className="text-primary hover:underline"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleSendReply}
            disabled={!replyMessage.trim() || isSendingReply}
            className="bg-primary text-white px-6 py-2 rounded-md text-sm font-semibold hover:bg-primary/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSendingReply ? "SENDING..." : "SEND"}
          </button>
        </div>
      </div>

      {isLoadingThread ? (
        <div className="bg-white border border-line rounded-xl px-6 py-4 text-sm text-muted">
          Loading conversation...
        </div>
      ) : null}

      {conversationThread.map((message, index) => (
        <div
          key={message.id ?? `${message.type}-${index}`}
          className="rounded-xl px-6 py-4 bg-white border break-words border-line"
        >
          <div className="flex sm:flex-row flex-col sm:items-center gap-1.5 sm:gap-2 mb-3">
            <span
              className="text-sm font-semibold text-primary"
            >
              {message.from}
            </span>
            <span
              className="text-xs text-muted"
            >
              {message.date}
            </span>

            {message.id && onEditMessage && onDeleteMessage ? (
              <div className="ml-auto flex items-center gap-2">
                {editingMessageId === message.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => saveMessageEdit(message.id)}
                      disabled={isSavingEdit || !editingText.trim()}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingEdit ? "SAVING..." : "SAVE"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      disabled={isSavingEdit}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-muted hover:bg-muted/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      CANCEL
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => startEditing(message)}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5"
                    >
                      EDIT
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMessage(message.id)}
                      disabled={Boolean(isDeletingMessageId)}
                      className="rounded-md border border-danger/30 px-2 py-1 text-[11px] font-semibold text-danger hover:bg-danger/5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isDeletingMessageId === message.id ? "DELETING..." : "DELETE"}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>
          <hr className="mb-3"/>
          {editingMessageId === message.id ? (
            <textarea
              value={editingText}
              onChange={(event) => setEditingText(event.target.value)}
              className="w-full min-h-[110px] resize-y rounded-md border border-line p-3 text-sm text-content outline-none focus:border-primary"
            />
          ) : (
            <p
              className="text-sm leading-relaxed whitespace-pre-line text-content"
            >
              {message.message}
            </p>
          )}
          {message.hasAttachment && (
            message.id && onDownloadAttachment ? (
              <button
                type="button"
                onClick={() => handleAttachmentClick(message)}
                disabled={isDownloadingMessageId === message.id}
                className="inline-flex items-center gap-2 mt-3 text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted"
              >
                <AttachFileIcon style={{ fontSize: "1rem" }} />
                <span>
                  {isDownloadingMessageId === message.id
                    ? "Preparing download..."
                    : message.attachmentName || "Download attachment"}
                </span>
              </button>
            ) : message.attachmentUrl ? (
              <a
                href={message.attachmentUrl}
                download={message.attachmentName || "attachment"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-sm text-primary hover:underline"
              >
                <AttachFileIcon style={{ fontSize: "1rem" }} />
                <span>{message.attachmentName || "Download attachment"}</span>
              </a>
            ) : (
              <div className="flex items-center gap-2 mt-3 text-sm text-muted">
                <AttachFileIcon style={{ fontSize: "1rem" }} />
                <span>{message.attachmentName || "Attachment included"}</span>
              </div>
            )
          )}
        </div>
      ))}

      <ConfirmationModal
        show={Boolean(messageToDeleteId)}
        onCancel={cancelDeleteMessage}
        onConfirm={confirmDeleteMessage}
        title="Delete Message"
        messages={["Are you sure you want to delete this message?"]}
        confirmText={isDeletingMessageId ? "DELETING..." : "DELETE"}
        cancelText="CANCEL"
        confirmColor="error"
        cancelColor="primary"
        isSubmitting={Boolean(isDeletingMessageId)}
      />
    </div>
  );
};

export default CaseDetail;
